import { useMemo, useState } from 'react';
import { Button } from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { supabase } from '../../lib/supabase';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  hasRequiredOAuthParams,
  isSafeRedirectUrl,
  readOAuthAuthorizeParams,
} from './authorizeParams';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;

/**
 * Consent screen for partner OAuth (GET /authorize on oauth-server redirects here).
 * Rendered only for authenticated users (AppRouter). Authorization (owner/admin of
 * the org) is enforced by oauth-server — this page is UX only.
 */
export function OAuthAuthorizePage() {
  const { organizations, currentOrganization, isLoading } = useOrganization();
  const params = useMemo(() => readOAuthAuthorizeParams(window.location.search), []);
  const adminOrgs = useMemo(
    () => organizations.filter((o) => o.role === 'owner' || o.role === 'admin'),
    [organizations],
  );
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orgId =
    selectedOrgId ||
    (currentOrganization && adminOrgs.some((o) => o.id === currentOrganization.id)
      ? currentOrganization.id
      : adminOrgs[0]?.id ?? '');

  const submit = async (decision: 'allow' | 'deny') => {
    if (!orgId || !SUPABASE_URL) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Sessão expirada. Faça login novamente.');
      const res = await fetch(`${SUPABASE_URL}/functions/v1/oauth-server/authorize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...params, organization_id: orgId, decision }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !isSafeRedirectUrl(body?.redirect_url)) {
        throw new Error(body?.error_description || 'Não foi possível autorizar a conexão.');
      }
      window.location.assign(body.redirect_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível autorizar a conexão.');
      setSubmitting(false);
    }
  };

  let content: React.ReactNode;
  if (!hasRequiredOAuthParams(params)) {
    content = <p className="text-sm text-secondary">Pedido de autorização inválido.</p>;
  } else if (isLoading) {
    content = <Spinner color="blue" size="lg" />;
  } else if (!adminOrgs.length) {
    content = (
      <p className="text-sm text-secondary">
        Apenas proprietários ou administradores de uma organização podem autorizar esta conexão.
      </p>
    );
  } else {
    content = (
      <>
        <p className="text-sm text-secondary">
          O aplicativo <strong>{params.client_id}</strong> quer enviar leads para a sua organização
          {params.scope ? ` (permissão: ${params.scope})` : ''}.
        </p>
        <label className="block text-sm font-medium text-default" htmlFor="oauth-org">
          Organização
          <select
            id="oauth-org"
            className="mt-1 w-full rounded-md border border-subtle bg-card p-2 text-sm"
            value={orgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            disabled={submitting}
          >
            {adminOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </label>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button onClick={() => submit('allow')} disabled={submitting || !orgId}>
            {submitting ? 'Autorizando...' : 'Autorizar'}
          </Button>
          <Button variant="outline" onClick={() => submit('deny')} disabled={submitting || !orgId}>
            Negar
          </Button>
        </div>
      </>
    );
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background p-s4">
      <section className="w-full max-w-md space-y-4 rounded-xl border border-subtle bg-card p-s6 shadow-sm">
        <h1 className="text-lg font-semibold text-default">Autorizar conexão</h1>
        {content}
      </section>
    </main>
  );
}
